import React from 'react';
import {
  List,
  ListItem,
  Typography,
  Divider,
  CircularProgress,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxHeight: '520px',
    overflowY: 'auto',
    padding: 0,
  },
  listItem: {
    cursor: 'pointer',
    padding: '7px 12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    transition: 'background 0.15s ease',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  activeItem: {
    backgroundColor: '#e8f0fe',
    borderLeft: '3px solid #002D55',
    paddingLeft: 9,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  name: {
    fontSize: '0.82rem',
    color: '#002D55',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    marginRight: 6,
  },
  nameUnread: {
    fontWeight: 700,
  },
  time: {
    fontSize: '0.7rem',
    color: '#999',
    flexShrink: 0,
  },
  timeUnread: {
    color: '#f79400',
    fontWeight: 600,
  },
  preview: {
    fontSize: '0.75rem',
    color: '#888',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginTop: 1,
  },
  previewUnread: {
    color: '#333',
    fontWeight: 600,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f79400',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.65rem',
    minWidth: 17,
    height: 17,
    borderRadius: 9,
    padding: '0 4px',
    marginLeft: 6,
    flexShrink: 0,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#999',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

export default function ConversationList({
  conversations,
  loading,
  onSelectConversation,
  activeConversationId,
}) {
  const classes = useStyles();

  const formatRelativeTime = (timestamp) => {
    const now = moment();
    const date = moment(timestamp);
    const diffMins = now.diff(date, 'minutes');
    const diffHours = now.diff(date, 'hours');
    const diffDays = now.diff(date, 'days');
    const diffWeeks = now.diff(date, 'weeks');
    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    if (diffWeeks < 5) return `${diffWeeks}w`;
    return date.format('MMM D');
  };

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!conversations || conversations.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="body2">No conversations yet.</Typography>
      </Box>
    );
  }

  return (
    <List className={classes.root} disablePadding>
      {conversations.map((conversation, index) => {
        const hasUnread = conversation.unread_count > 0;
        const isActive = activeConversationId === conversation.id && hasUnread;

        return (
          <React.Fragment key={conversation.id}>
            <ListItem
              className={`${classes.listItem} ${isActive ? classes.activeItem : ''}`}
              onClick={() => onSelectConversation(conversation)}
              button
              disableGutters
            >
              <div className={classes.row}>
                <span className={`${classes.name} ${hasUnread ? classes.nameUnread : ''}`}>
                  {conversation.other_participant_name}
                </span>
                <span className={`${classes.time} ${hasUnread ? classes.timeUnread : ''}`}>
                  {formatRelativeTime(conversation.last_activity)}
                </span>
              </div>
              <div className={classes.row} style={{ alignItems: 'center' }}>
                <span className={`${classes.preview} ${hasUnread ? classes.previewUnread : ''}`}>
                  {conversation.last_message
                    ? conversation.last_message.slice(0, 55) + (conversation.last_message.length > 55 ? '…' : '')
                    : 'No messages yet'}
                </span>
                {hasUnread && (
                  <span className={classes.badge}>{conversation.unread_count}</span>
                )}
              </div>
            </ListItem>
            {index < conversations.length - 1 && <Divider component="li" />}
          </React.Fragment>
        );
      })}
    </List>
  );
}